import type { Vero1ReasonCatalog } from "shared/interfaces/api/vero1";

/**
 * TODO: remove once sayvero/backend#4063 ships. That PR returns the catalog on
// When swapping to the served catalog: the axios layer camelCases response
// KEYS — `directions` is keyed by the codes themselves and will be garbled.
// Consume codes from `by_category` (values are untouched) or fetch raw.
 * the checklist GET as `reason_codes`; until it lands the editor has no way to
 * populate the picker, so this mirrors REASON_CODES / CATEGORY_TO_AREAS from
 * vero/constants/vero1_checklist.py.
 *
 * Keep in sync until deleted: the model validates reason_code on save, so a
 * code that drifts from the backend surfaces as a failed override, not a
 * display bug.
 */

export type ReasonDirection = "favorable" | "adverse" | "neutral" | "any";

interface ReasonSpec {
  area: string;
  direction: ReasonDirection;
}

const REASON_CODES: Record<string, ReasonSpec> = {
  INCOME_MANUALLY_VERIFIED: { area: "income", direction: "favorable" },
  INCOME_INSUFFICIENT: { area: "income", direction: "adverse" },
  INCOME_DOCS_INSUFFICIENT: { area: "income", direction: "adverse" },
  INCOME_ABOVE_PROGRAM_MAX: { area: "income", direction: "adverse" },
  INCOME_SOURCE_NOT_ACCEPTED: { area: "income", direction: "adverse" },
  INCOME_BELOW_PROGRAM_MIN: { area: "income", direction: "adverse" },
  NET_TO_GROSS_RECALCULATED: { area: "income", direction: "neutral" },
  CREDIT_SCORE_CONDITIONAL_BAND: { area: "credit", direction: "adverse" },
  THIN_FILE_NO_SCORE: { area: "credit", direction: "adverse" },
  GUARANTOR_SCORE_USED: { area: "credit", direction: "neutral" },
  RENTAL_COLLECTION_IDENTIFIED: { area: "collections", direction: "adverse" },
  COLLECTIONS_OVER_POLICY: { area: "collections", direction: "adverse" },
  COLLECTIONS_DISREGARDED_POLICY: {
    area: "collections",
    direction: "favorable",
  },
  UTILITY_COLLECTION_CONDITIONAL: { area: "collections", direction: "adverse" },
  EVICTION_RECORD_VERIFIED: { area: "rental", direction: "adverse" },
  RECORD_NOT_APPLICABLE: { area: "background", direction: "favorable" },
  CRIMINAL_RECORD_POLICY_MATCH: { area: "criminal", direction: "adverse" },
  SUSPECT_SSN: { area: "identity", direction: "adverse" },
  IDENTITY_NOT_VERIFIABLE: { area: "identity", direction: "adverse" },
  CPN_CHECK_PERFORMED: { area: "identity", direction: "neutral" },
  NO_SSN_ALTERNATIVE_SCREENING: { area: "identity", direction: "neutral" },
  ID_REVERIFICATION_PERFORMED: { area: "identity", direction: "neutral" },
  GUARANTOR_FINANCIALS_NOT_US: { area: "guarantor", direction: "adverse" },
  GUARANTOR_INCOME_USED: { area: "guarantor", direction: "neutral" },
  APPLICANT_UNRESPONSIVE: { area: "deal", direction: "neutral" },
  OTHER: { area: "any", direction: "any" },
};

/** Mirrors CATEGORY_TO_AREAS — a category draws on several areas. */
const CATEGORY_TO_AREAS: Record<string, string[]> = {
  identity: ["identity", "any"],
  income: ["income", "guarantor", "any"],
  credit: ["credit", "collections", "guarantor", "any"],
  rental: ["rental", "collections", "background", "any"],
  criminal: ["criminal", "background", "any"],
};

/** Codes offerable on a row, scoped to its category. OTHER is area-any, so it
 *  is always included as the escape hatch. */
const BAND_CODE_SUFFIX = "_CONDITIONAL_BAND";

export const reasonCodesForCategory = (
  category?: string,
  { hasWarnOption = false }: { hasWarnOption?: boolean } = {},
  served?: Vero1ReasonCatalog
): string[] => {
  // the served catalog is authoritative once the backend provides it — the
  // hardcoded list below is the pre-merge fallback and dies with it
  const base = served
    ? served.byCategory[category ?? ""] ?? served.uncategorized
    : Object.entries(REASON_CODES)
        .filter(([, spec]) =>
          (CATEGORY_TO_AREAS[category ?? ""] ?? ["any"]).includes(spec.area)
        )
        .map(([code]) => code);
  return base.filter(
    // band codes need a configured warn outcome on the row
    (code) => hasWarnOption || !code.endsWith(BAND_CODE_SUFFIX)
  );
};

/** Leasing-facing wording per code, mirroring the backend's
 *  REASON_CODE_DISPLAY. The served catalog wins where it reaches the caller;
 *  this carries the surfaces that render a stored code without one. */
const REASON_CODE_DISPLAY: Record<string, string> = {
  INCOME_MANUALLY_VERIFIED: "Income verified through manual review",
  INCOME_INSUFFICIENT: "Verified income below the required amount",
  INCOME_DOCS_INSUFFICIENT: "Insufficient documentation to verify income",
  INCOME_ABOVE_PROGRAM_MAX: "Income exceeds the program maximum",
  INCOME_SOURCE_NOT_ACCEPTED: "Income source not accepted under program guidelines",
  INCOME_BELOW_PROGRAM_MIN: "Income below the program minimum",
  NET_TO_GROSS_RECALCULATED: "Income recalculated from net to gross",
  CREDIT_SCORE_CONDITIONAL_BAND: "Credit score within the conditional range",
  THIN_FILE_NO_SCORE: "Insufficient credit history to generate a score",
  GUARANTOR_SCORE_USED: "Guarantor credit used for evaluation",
  RENTAL_COLLECTION_IDENTIFIED: "Rental-related collection identified",
  COLLECTIONS_OVER_POLICY: "Collections balance exceeds policy threshold",
  COLLECTIONS_DISREGARDED_POLICY: "Collections disregarded under policy",
  UTILITY_COLLECTION_CONDITIONAL: "Utility collection within the conditional range",
  EVICTION_RECORD_VERIFIED: "Eviction record verified",
  RECORD_NOT_APPLICABLE: "No applicable record found",
  CRIMINAL_RECORD_POLICY_MATCH: "Criminal record matches policy criteria",
  SUSPECT_SSN: "Social Security number could not be validated",
  IDENTITY_NOT_VERIFIABLE: "Identity could not be verified",
  CPN_CHECK_PERFORMED: "Identity number reviewed",
  NO_SSN_ALTERNATIVE_SCREENING: "Alternative screening completed without a Social Security number",
  ID_REVERIFICATION_PERFORMED: "Identity re-verification completed",
  GUARANTOR_FINANCIALS_NOT_US: "Guarantor financials are outside the United States",
  GUARANTOR_INCOME_USED: "Guarantor income used for evaluation",
  APPLICANT_UNRESPONSIVE: "Applicant did not respond to requests",
  OTHER: "Other",
};

/** The wording the agent reads — never the raw code. Falls back to the code
 *  itself only for one the catalog and the mirror both miss, so an unknown
 *  code still renders something rather than blank. */
export const reasonLabel = (
  code: string,
  served?: Vero1ReasonCatalog
): string =>
  served?.labels?.find((entry) => entry.code === code)?.label ??
  REASON_CODE_DISPLAY[code] ??
  code;

export const reasonDirection = (code: string): ReasonDirection =>
  REASON_CODES[code]?.direction ?? "neutral";

export const OTHER_REASON_CODE = "OTHER";

/** Spec §4.2: OTHER reveals a required short label, one line. */
export const OTHER_LABEL_MAX_LENGTH = 45;
